/**
 * DEC-001: Experience View Component
 * 
 * Renders custom UX layouts based on DocTypeView configuration.
 * Integrates with existing TabRenderer and adds CustomFieldBucket.
 * 
 * NEW (v1): For doctypes with DocViewSchema, reads layout from schema.
 * This is the AI-first approach - schema is the single source of truth.
 * 
 * Converts FormViewConfig.sections to TabStructure for custom layouts.
 * Falls back to meta-driven layout if no custom sections are defined.
 * 
 * Special handling for Shopify Sales Order view with custom tab navigation
 * integrated with global FloatingNav via DetailPageTabsContext.
 */

import React from 'react';
import type { DocTypeView } from '@/lib/views/types';
import type { DocTypeSchema } from '@/lib/erp/meta';
import { TabRenderer } from '@/features/inventory/item-detail/components/TabRenderer';
import { buildTabsFromMeta } from '@/features/inventory/item-detail/utils/buildTabsFromMeta';
import { convertFormViewToTabStructure } from '@/lib/views/utils/formViewToTabs';
import { CustomFieldBucket } from './CustomFieldBucket';
import { RelationWidget } from './RelationWidget';
import { computeUnmappedFields, computeUnmappedFieldsFromSchema, mapViewStyleToLayoutStyle } from '@/lib/views/utils';
import type { TabStructure } from '@/features/inventory/item-detail/utils/buildTabsFromMeta';
import { DynamicViewProvider } from '@/contexts/DynamicViewContext';
import { useLanguage } from '@/i18n/useLanguage';
import { useDashboardTab, DASHBOARD_TAB_ID } from '@/features/common/doc-dashboard/useDashboardTab';
import { DocDashboardTab } from '@/features/common/doc-dashboard/DocDashboardTab';
import { useConnectionsTab, CONNECTIONS_TAB_ID } from '@/features/common/doc-connections/useConnectionsTab';
import { ConnectionsTab } from '@/features/common/doc-connections/ConnectionsTab';
import { useQuickConnectionsTab, QUICK_CONNECTIONS_TAB_ID } from '@/features/common/doc-connections/useQuickConnectionsTab';
import { QuickConnectionsTab } from '@/features/common/doc-connections/QuickConnectionsTab';
import { useItemPriceTab, ITEM_PRICE_TAB_ID } from '@/features/inventory/item-price/useItemPriceTab';
import { ItemPriceTab } from '@/features/inventory/item-price/ItemPriceTab';
import { useLoyaltyTab, LOYALTY_TAB_ID } from '@/features/customers/customer-detail/useLoyaltyTab';
import { LoyaltyTab } from '@/features/customers/customer-detail/LoyaltyTab';
import { useAddressesTab, ADDRESSES_TAB_ID } from '@/features/customers/customer-detail/useAddressesTab';
import { AddressesTab } from '@/features/customers/customer-detail/AddressesTab';
import { FinancialTab } from '@/features/customers/customer-detail/FinancialTab';
import { useDetailPageTabs } from '@/contexts/DetailPageTabsContext';
import { usePreferencesStore } from '@/lib/store/preferencesStore';
import { useMediaQuery } from '@/hooks/useMediaQuery';

// DocViewSchema v1: Schema-driven layout
import { hasDocViewSchema, getDocViewSchema } from '@/lib/views/docViewSchemas';
import { convertSchemaToTabs } from '@/lib/views/utils/schemaToTabs';

interface ExperienceViewProps {
  view: DocTypeView;
  meta: DocTypeSchema;
  formData: Record<string, any>;
  onFieldChange: (fieldname: string, value: any) => void;
  disabled?: boolean;
  onSave?: () => void;
  onSubmit?: (patch: Record<string, any>) => Promise<void> | void;
  onSaveSuccess?: (updatedDoc: Record<string, any>) => void;
  isSaving?: boolean;
  resetKey?: string | number;
  documentName?: string; // Document identifier (name/id) - required for attachment fields
  isNew?: boolean; // Whether this is a new document (create mode)
  /** Field-level error map from server insert failure (Plan 166 Epic 02) */
  fieldErrors?: Record<string, string>;
  /** Clear a single field error when user starts editing */
  onClearFieldError?: (fieldname: string) => void;
}

// Tab-to-section mapping for Shopify Sales Order view
const SHOPIFY_SO_TAB_SECTION_MAP: Record<string, string[]> = {
  details: [
    'order-overview', // Compact info: dates, channel, payment, notes
    // 'customer-channel' — shown in ShopifyOrderHeader widget
    'items',          // Line items table
    'totals',         // Receipt: subtotal → discount → shipping → tax → grand total
  ],
  'address-contact': [
    'address-contact',   // contact person + contact info
    'billing-address',   // separate billing address card
    'shipping-address',  // separate shipping address card
    'shipping-fulfillment', // warehouse & status
  ],
  terms: ['terms'],
  'more-info': [
    'status-summary',      // Fulfillment/billing percentages
    'taxes_section',       // Tax templates and configuration
    'shipping_section',    // Shipping rules and Incoterms
    'discounts',           // Additional discount configuration
    'currency-price-list', // Currency & price list config
    'commission',
    'sales-team',
    'more-info',
  ],
};

// DEPRECATED: Tab-to-section mapping for Shopify Item view
// DocViewSchema v1: Item now uses schema-driven tabs from docViewSchemas/item.ts
// This map is bypassed when DocViewSchema exists (isShopifyItemView checks for !docViewSchema)
// TODO: Remove this once all doctypes migrate to DocViewSchema
const SHOPIFY_ITEM_TAB_SECTION_MAP: Record<string, string[]> = {
  details: [
    'item-overview',
    'item-details',
    'pricing',
    'inventory',
    'status-summary',
  ],
  inventory: ['inventory-tab'],
  types: ['types-tab'],
  variants: ['variants-tab'],
  accounting: ['accounting-tab'],
  purchase: ['purchase-tab'],
  sales: ['sales-tab'],
  tax: ['tax-tab'],
  quality: ['quality-tab'],
  manufacturing: ['manufacturing-tab'],
  'more-info': ['more-info'], // Auto-generated section for unmapped meta fields
};

// Tab-to-section mapping for Shopify Stock Entry view
const SHOPIFY_STOCK_ENTRY_TAB_SECTION_MAP: Record<string, string[]> = {
  details: [
    'stock-entry-overview',
    'stock-entry-details',
    'items',
    'status-summary',
    'stock-entry-timeline',
    'stock-entry-actions',
  ],
  'additional-details': [
    'stock-entry-additional-details',
  ],
  'more-info': [
    'stock-entry-more-info',
    'more-info', // Auto-generated section for unmapped meta fields
  ],
};

// Tab-to-section mapping for Shopify Item Price view
const SHOPIFY_ITEM_PRICE_TAB_SECTION_MAP: Record<string, string[]> = {
  details: [
    'item-price-basic',
    'item-price-type-validity',
    'item-price-context',
    'more-info', // Auto-generated section for unmapped meta fields
  ],
};

// Tab-to-section mapping for Shopify Pricing Rule view
const SHOPIFY_PRICING_RULE_TAB_SECTION_MAP: Record<string, string[]> = {
  details: [
    'pricing-rule-basic',
    'pricing-rule-discount',
    'pricing-rule-validity',
    'pricing-rule-context',
    'more-info', // Auto-generated section for unmapped meta fields
  ],
};

// Tab-to-section mapping for Shopify Customer view
const SHOPIFY_CUSTOMER_TAB_SECTION_MAP: Record<string, string[]> = {
  details: [
    'customer-overview',
    'customer-contact',
  ],
  financial: [
    'customer-defaults',
    'customer-credit',
    // 'customer-loyalty' moved to the dedicated Loyalty tab (points, tier, history)
  ],
  'more-info': [
    'customer-tax',
    'customer-classification',
    'customer-details',
    'customer-flags',
    'more-info', // Auto-generated section for unmapped meta fields
  ],
};

export function ExperienceView({
  view,
  meta,
  formData,
  onFieldChange,
  disabled,
  onSave,
  onSubmit,
  onSaveSuccess,
  isSaving,
  resetKey,
  documentName,
  isNew = false,
  fieldErrors,
  onClearFieldError,
}: ExperienceViewProps) {
  // ============================================
  // ALL HOOKS FIRST (unconditional)
  // ============================================
  
  // Detail density: read from store with selectors, force default on mobile
  const detailDensity = usePreferencesStore((s) => s.detailDensity);
  const detailTextSize = usePreferencesStore((s) => s.detailTextSize);
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const effectiveDensity = isDesktop ? detailDensity : 'default';

  // Global tabs context (used by FloatingNav)
  const { activeTab, resetForScope } = useDetailPageTabs();
  const doctype = meta?.name || '';
  const docName = documentName || formData?.name;

  // Reset tab state when switching between doctypes/documents.
  React.useEffect(() => {
    const stableDoc = documentName ?? (typeof formData?.name === "string" ? formData.name : "new");
    resetForScope(`${doctype}:${isNew ? "new" : stableDoc || "new"}`);
  }, [doctype, documentName, formData?.name, isNew, resetForScope]);
  
  // Register Dashboard tab (automatically adds to tab list, positioned before Inventory)
  useDashboardTab({
    doctype,
    name: docName,
    enabled: true, // Always enabled in experience view
  });
  
  // Register Connection (Quick Links) tab (automatically adds to tab list, before System Connections)
  useQuickConnectionsTab({
    doctype,
    name: docName,
    enabled: true, // Always enabled in experience view
  });
  
  // Register Item Price tab (only for Item doctype)
  useItemPriceTab({
    doctype,
    name: docName,
    enabled: true, // Always enabled in experience view
  });

  // Register Loyalty tab (only for Customer doctype)
  useLoyaltyTab({
    doctype,
    name: docName,
    enabled: true,
  });

  // Register Addresses tab (Customer only — every delivery address they've used)
  useAddressesTab({
    doctype,
    name: docName,
    enabled: true,
  });

  // Register Connections tab (automatically adds to tab list)
  useConnectionsTab({
    doctype,
    name: docName,
    enabled: true, // Always enabled in experience view
  });
  
  // TabRenderer will set tabs in context

  // DocViewSchema v1: Check if this doctype has a schema
  // If so, use schema-driven tabs instead of legacy FormViewConfig
  // NOTE: Must be called unconditionally (before any early returns) to satisfy Rules of Hooks
  const docViewSchema = React.useMemo(() => {
    if (hasDocViewSchema(view.doctype)) {
      return getDocViewSchema(view.doctype);
    }
    return null;
  }, [view.doctype]);

  // Guard: Ensure meta and formData are valid
  // NOTE: This check happens AFTER all hooks to satisfy Rules of Hooks
  const isValidMeta = meta && meta.fields && meta.fields.length > 0;

  // Convert FormViewConfig.sections to TabStructure if sections exist
  // For doctypes with DocViewSchema, use schema-driven tabs (AI-first approach)
  // Otherwise fall back to meta-driven layout
  // NOTE: Must be called unconditionally (before any early returns) to satisfy Rules of Hooks
  const tabs = React.useMemo(() => {
    // Early return for invalid meta (safe because this is inside useMemo, not a component return)
    if (!isValidMeta) {
      return [];
    }

    try {
      // DocViewSchema v1: Use schema for doctypes that have it
      // Check for create variant first if this is a new document
      if (docViewSchema) {
        // Prefer create variant for new documents, fallback to experience
        const variantConfig = isNew && docViewSchema.views.create
          ? docViewSchema.views.create
          : docViewSchema.views.experience;

        if (variantConfig) {
        if (import.meta.env.DEV) {
            console.log(
              `[ExperienceView] Using DocViewSchema ${isNew && docViewSchema.views.create ? 'create' : 'experience'} variant for ${view.doctype}`
            );
        }
          const schemaTabs = convertSchemaToTabs(variantConfig, meta, formData || {});
        // Ensure we always return an array
        return Array.isArray(schemaTabs) ? schemaTabs : [];
        }
      }

      // Legacy path: use FormViewConfig
      if (view.formView?.sections && view.formView.sections.length > 0) {
        const formTabs = convertFormViewToTabStructure(view.formView, meta, formData || {});
        return Array.isArray(formTabs) ? formTabs : [];
      }
      const metaTabs = buildTabsFromMeta(meta);
      return Array.isArray(metaTabs) ? metaTabs : [];
    } catch (error) {
      console.error('[ExperienceView] Error converting tabs:', error);
      // Fallback to meta-driven tabs on error
      try {
        return buildTabsFromMeta(meta);
      } catch (fallbackError) {
        console.error('[ExperienceView] Fallback also failed:', fallbackError);
        return [];
      }
    }
  }, [meta, view.formView, formData, docViewSchema, view.doctype, isValidMeta, isNew]);

  // Compute unmapped fields for CustomFieldBucket (legacy FormViewConfig only)
  // For schema-driven doctypes, unmapped fields are handled via _collectUnmapped sections
  // NOTE: Must be called unconditionally (before any early returns) to satisfy Rules of Hooks
  const { mappedFieldnames, unmappedFieldnames } = React.useMemo(() => {
    // Early return for invalid meta (safe because this is inside useMemo, not a component return)
    if (!isValidMeta) {
      return { mappedFieldnames: new Set<string>(), unmappedFieldnames: [] };
    }

    // For schema-driven views, compute unmapped fields for debugging/logs
    // (unmapped fields are already populated in _collectUnmapped sections via schemaToTabs)
    if (docViewSchema) {
      const variantConfig = isNew && docViewSchema.views.create
        ? docViewSchema.views.create
        : docViewSchema.views.experience;
      if (variantConfig) {
        return computeUnmappedFieldsFromSchema(meta, variantConfig);
      }
    }
    // For legacy FormViewConfig, use existing computation
    return computeUnmappedFields(meta, view.formView);
  }, [meta, view.formView, docViewSchema, isValidMeta, isNew]);

  const unmappedFields = React.useMemo(() => {
    if (!isValidMeta) {
      return [];
    }
    return meta.fields.filter(f => unmappedFieldnames.includes(f.fieldname));
  }, [meta.fields, unmappedFieldnames, isValidMeta]);

  // Only show CustomFieldBucket for legacy FormViewConfig doctypes
  // Schema-driven doctypes use _collectUnmapped sections instead
  const shouldShowCustomFieldBucket = 
    !docViewSchema?.views.experience && // Only for non-schema doctypes
    view.formView?.customFieldBucket?.enabled &&
    mappedFieldnames.size > 0 && // Only show if we actually have mapped fields
    unmappedFields.length > 0;

  // ============================================
  // DERIVED VALUES (after hooks)
  // ============================================

  // Detect Shopify Sales Order view
  // Sales Order uses legacy FormViewConfig, not DocViewSchema
  const isShopifyOrderView =
    view.style === 'shopify' && view.doctype === 'Sales Order';

  // Detect Shopify Item view
  // NOTE: When DocViewSchema exists, we skip the legacy tab mapping
  const isShopifyItemView =
    view.style === 'shopify' && view.doctype === 'Item' && !docViewSchema;

  // Detect Shopify Stock Entry view
  const isShopifyStockEntryView =
    view.style === 'shopify' && view.doctype === 'Stock Entry';

  // Detect Shopify Item Price view
  const isShopifyItemPriceView =
    view.style === 'shopify' && view.doctype === 'Item Price';

  // Detect Shopify Pricing Rule view (skip in create mode — wizard uses raw tabs)
  const isShopifyPricingRuleView =
    view.style === 'shopify' && view.doctype === 'Pricing Rule' && !isNew;

  // Detect Shopify Customer view
  const isShopifyCustomerView =
    view.style === 'shopify' && view.doctype === 'Customer';

  // Map view style to layout style
  const layoutStyle = mapViewStyleToLayoutStyle(view.style);

  // All sections from all tabs (for filtering)
  const allSections = React.useMemo(
    () => tabs.flatMap(tab => tab.sections),
    [tabs]
  );

  // Create custom tabs for Shopify Sales Order view
  const shopifyOrderTabs = React.useMemo(() => {
    if (!isShopifyOrderView) return null;

    const customTabs: TabStructure[] = [];

    // Create each custom tab with filtered sections
    for (const [tabName, sectionNames] of Object.entries(SHOPIFY_SO_TAB_SECTION_MAP)) {
      const sectionsForTab = allSections
        .filter(section => {
          // Include if section name matches allowed list (order-overview is now included)
          // Also include auto-generated "more-info-*" sections in the "more-info" tab
          if (tabName === 'more-info' && section.name.startsWith('more-info-')) {
            return true;
          }
        return sectionNames.includes(section.name);
        })
        // Sort sections to match the order specified in sectionNames
        .sort((a, b) => {
          const indexA = sectionNames.indexOf(a.name);
          const indexB = sectionNames.indexOf(b.name);
          // Auto-generated sections go to the end
          if (indexA === -1 && indexB === -1) return 0;
          if (indexA === -1) return 1;
          if (indexB === -1) return -1;
          return indexA - indexB;
      });

      if (sectionsForTab.length > 0) {
        customTabs.push({
          name: tabName,
          label:
            tabName === 'details'
              ? 'Details'
              : tabName === 'address-contact'
              ? 'Address & Contact'
              : tabName === 'terms'
              ? 'Terms'
              : 'More Info',
          sections: sectionsForTab,
        });
      }
    }

    return customTabs.length > 0 ? customTabs : null;
  }, [isShopifyOrderView, allSections]);

  // Create custom tabs for Shopify Item view
  const shopifyItemTabs = React.useMemo(() => {
    if (!isShopifyItemView) return null;

    const customTabs: TabStructure[] = [];

    // Create each custom tab with filtered sections
    for (const [tabName, sectionNames] of Object.entries(SHOPIFY_ITEM_TAB_SECTION_MAP)) {
      // Filter sections for this tab
      let sectionsForTab = allSections
        .filter(section => {
          // Include if section name matches allowed list
          // Also include auto-generated "more-info-*" sections in the "more-info" tab
          if (tabName === 'more-info' && section.name.startsWith('more-info-')) {
            return true;
          }
          return sectionNames.includes(section.name);
        })
        // Sort sections to match the order specified in sectionNames
        .sort((a, b) => {
          const indexA = sectionNames.indexOf(a.name);
          const indexB = sectionNames.indexOf(b.name);
          // Auto-generated sections go to the end
          if (indexA === -1 && indexB === -1) return 0;
          if (indexA === -1) return 1;
          if (indexB === -1) return -1;
          return indexA - indexB;
        });

      // Check conditional visibility for sections in this tab
      // Find the section config to check its conditional visibility
      if (view.formView?.sections) {
        sectionsForTab = sectionsForTab.filter(section => {
          const sectionConfig = view.formView!.sections.find(s => s.id === section.name);
          if (sectionConfig?._conditionalVisibility) {
            const { field, value } = sectionConfig._conditionalVisibility;
            
            // Special handling for variants tab: show if item has variants OR is itself a variant
            if (field === 'has_variants_or_is_variant') {
              const hasVariants = formData?.has_variants === 1 || formData?.has_variants === true;
              const isVariant = !!(formData?.variant_of);
              return hasVariants || isVariant;
            }
            
            const fieldValue = formData?.[field];
            // Include section only if condition is met
            // Handle both number and boolean comparisons
            if (typeof value === 'number' && typeof fieldValue === 'number') {
              return fieldValue === value;
            }
            if (typeof value === 'boolean' && typeof fieldValue === 'boolean') {
              return fieldValue === value;
            }
            // Handle 1/0 as boolean
            if (value === 1 && (fieldValue === 1 || fieldValue === true)) {
              return true;
            }
            if (value === 0 && (fieldValue === 0 || fieldValue === false)) {
              return true;
            }
            return fieldValue === value;
          }
          // No conditional visibility - always include
          return true;
        });
      }

      // Skip tab if no sections after filtering (e.g., variants tab when has_variants !== 1)
      if (sectionsForTab.length === 0) {
        continue;
      }

      // Capitalize tab labels
      const tabLabel = tabName === 'details' 
        ? 'Details' 
        : tabName.charAt(0).toUpperCase() + tabName.slice(1);
      
      customTabs.push({
        name: tabName,
        label: tabLabel,
        sections: sectionsForTab,
      });
    }

    return customTabs.length > 0 ? customTabs : null;
  }, [isShopifyItemView, allSections, formData, view.formView]);

  // Create custom tabs for Shopify Stock Entry view
  const shopifyStockEntryTabs = React.useMemo(() => {
    if (!isShopifyStockEntryView) return null;

    const customTabs: TabStructure[] = [];

    // Create each custom tab with filtered sections
    for (const [tabName, sectionNames] of Object.entries(SHOPIFY_STOCK_ENTRY_TAB_SECTION_MAP)) {
      // Filter sections for this tab
      const sectionsForTab = allSections
        .filter(section => {
          // Include if section name matches allowed list
          // Also include auto-generated "more-info-*" sections in the "more-info" tab
          if (tabName === 'more-info' && section.name.startsWith('more-info-')) {
            return true;
          }
          return sectionNames.includes(section.name);
        })
        // Sort sections to match the order specified in sectionNames
        .sort((a, b) => {
          const indexA = sectionNames.indexOf(a.name);
          const indexB = sectionNames.indexOf(b.name);
          // Auto-generated sections go to the end
          if (indexA === -1 && indexB === -1) return 0;
          if (indexA === -1) return 1;
          if (indexB === -1) return -1;
          return indexA - indexB;
        });

      // Skip tab if no sections after filtering
      if (sectionsForTab.length === 0) {
        continue;
      }

      // Capitalize tab labels
      const tabLabel = tabName === 'details' 
        ? 'Details' 
        : tabName === 'additional-details'
        ? 'Additional Details'
        : tabName === 'more-info'
        ? 'More Information'
        : tabName.charAt(0).toUpperCase() + tabName.slice(1);
      
      customTabs.push({
        name: tabName,
        label: tabLabel,
        sections: sectionsForTab,
      });
    }

    return customTabs.length > 0 ? customTabs : null;
  }, [isShopifyStockEntryView, allSections]);

  // Create custom tabs for Shopify Item Price view
  const shopifyItemPriceTabs = React.useMemo(() => {
    if (!isShopifyItemPriceView) return null;

    const customTabs: TabStructure[] = [];

    // Create each custom tab with filtered sections
    for (const [tabName, sectionNames] of Object.entries(SHOPIFY_ITEM_PRICE_TAB_SECTION_MAP)) {
      // Filter sections for this tab
      const sectionsForTab = allSections
        .filter(section => {
          // Include if section name matches allowed list
          // Also include auto-generated "more-info-*" sections in the "details" tab for Item Price
          if (tabName === 'details' && section.name.startsWith('more-info-')) {
            if (import.meta.env.DEV) {
              console.log('[ItemPriceView] Including auto-generated section', {
                sectionName: section.name,
                sectionLabel: section.label,
                fieldCount: section.columns.reduce((sum, col) => sum + col.fields.length, 0),
              });
            }
            return true;
          }
          return sectionNames.includes(section.name);
        })
        // Sort sections to match the order specified in sectionNames
        .sort((a, b) => {
          const indexA = sectionNames.indexOf(a.name);
          const indexB = sectionNames.indexOf(b.name);
          // Auto-generated sections go to the end
          if (indexA === -1 && indexB === -1) return 0;
          if (indexA === -1) return 1;
          if (indexB === -1) return -1;
          return indexA - indexB;
        });

      // Skip tab if no sections after filtering
      if (sectionsForTab.length === 0) {
        continue;
      }

      // Capitalize tab labels
      const tabLabel = tabName === 'details' 
        ? 'Details' 
        : tabName.charAt(0).toUpperCase() + tabName.slice(1);
      
      customTabs.push({
        name: tabName,
        label: tabLabel,
        sections: sectionsForTab,
      });
    }

    return customTabs.length > 0 ? customTabs : null;
  }, [isShopifyItemPriceView, allSections]);

  // Create custom tabs for Shopify Pricing Rule view
  const shopifyPricingRuleTabs = React.useMemo(() => {
    if (!isShopifyPricingRuleView) return null;

    const customTabs: TabStructure[] = [];

    // Create each custom tab with filtered sections
    for (const [tabName, sectionNames] of Object.entries(SHOPIFY_PRICING_RULE_TAB_SECTION_MAP)) {
      // Filter sections for this tab
      const sectionsForTab = allSections
        .filter(section => {
          // Include if section name matches allowed list
          // Also include auto-generated "more-info-*" sections in the "details" tab for Pricing Rule
          if (tabName === 'details' && section.name.startsWith('more-info-')) {
            if (import.meta.env.DEV) {
              console.log('[PricingRuleView] Including auto-generated section', {
                sectionName: section.name,
                sectionLabel: section.label,
                fieldCount: section.columns.reduce((sum, col) => sum + col.fields.length, 0),
              });
            }
            return true;
          }
          return sectionNames.includes(section.name);
        })
        // Sort sections to match the order specified in sectionNames
        .sort((a, b) => {
          const indexA = sectionNames.indexOf(a.name);
          const indexB = sectionNames.indexOf(b.name);
          // Auto-generated sections go to the end
          if (indexA === -1 && indexB === -1) return 0;
          if (indexA === -1) return 1;
          if (indexB === -1) return -1;
          return indexA - indexB;
        });

      // Skip tab if no sections after filtering
      if (sectionsForTab.length === 0) {
        continue;
      }

      // Capitalize tab labels
      const tabLabel = tabName === 'details' 
        ? 'Details' 
        : tabName.charAt(0).toUpperCase() + tabName.slice(1);
      
      customTabs.push({
        name: tabName,
        label: tabLabel,
        sections: sectionsForTab,
      });
    }

    return customTabs.length > 0 ? customTabs : null;
  }, [isShopifyPricingRuleView, allSections]);

  // Create custom tabs for Shopify Customer view
  const shopifyCustomerTabs = React.useMemo(() => {
    if (!isShopifyCustomerView) return null;

    const customTabs: TabStructure[] = [];

    for (const [tabName, sectionNames] of Object.entries(SHOPIFY_CUSTOMER_TAB_SECTION_MAP)) {
      const sectionsForTab = allSections
        .filter(section => {
          // Auto-generated "more-info-*" sections go to the "more-info" tab
          if (tabName === 'more-info' && section.name.startsWith('more-info-')) {
            return true;
          }
          return sectionNames.includes(section.name);
        })
        // Sort sections to match the order specified in sectionNames
        .sort((a, b) => {
          const indexA = sectionNames.indexOf(a.name);
          const indexB = sectionNames.indexOf(b.name);
          if (indexA === -1 && indexB === -1) return 0;
          if (indexA === -1) return 1;
          if (indexB === -1) return -1;
          return indexA - indexB;
        });

      if (sectionsForTab.length === 0) continue;

      customTabs.push({
        name: tabName,
        label:
          tabName === 'details'
            ? 'Details'
            : tabName === 'financial'
            ? 'Financial'
            : 'More Info',
        sections: sectionsForTab,
      });
    }

    return customTabs.length > 0 ? customTabs : null;
  }, [isShopifyCustomerView, allSections]);

  // Tabs to pass to TabRenderer
  // For Shopify views: pass all custom tabs (TabRenderer will handle active tab filtering)
  // For other views: pass tabs as-is
  const tabsToRender = React.useMemo(() => {
    if (isShopifyOrderView && shopifyOrderTabs && shopifyOrderTabs.length > 0) {
      // Pass all custom tabs to TabRenderer
      // TabRenderer will set them in context (for FloatingNav) and only render the active tab
      return shopifyOrderTabs;
    }

    if (isShopifyItemView && shopifyItemTabs && shopifyItemTabs.length > 0) {
      // Pass all custom tabs to TabRenderer for Item view
      return shopifyItemTabs;
    }

    if (isShopifyStockEntryView && shopifyStockEntryTabs && shopifyStockEntryTabs.length > 0) {
      // Pass all custom tabs to TabRenderer for Stock Entry view
      return shopifyStockEntryTabs;
    }

    if (isShopifyItemPriceView && shopifyItemPriceTabs && shopifyItemPriceTabs.length > 0) {
      // Pass all custom tabs to TabRenderer for Item Price view
      return shopifyItemPriceTabs;
    }

    if (isShopifyPricingRuleView && shopifyPricingRuleTabs && shopifyPricingRuleTabs.length > 0) {
      // Pass all custom tabs to TabRenderer for Pricing Rule view
      return shopifyPricingRuleTabs;
    }

    if (isShopifyCustomerView && shopifyCustomerTabs && shopifyCustomerTabs.length > 0) {
      // Pass all custom tabs to TabRenderer for Customer view
      return shopifyCustomerTabs;
    }

    // Normal behavior: pass tabs as-is
    return tabs;
  }, [isShopifyOrderView, shopifyOrderTabs, isShopifyItemView, shopifyItemTabs, isShopifyStockEntryView, shopifyStockEntryTabs, isShopifyItemPriceView, shopifyItemPriceTabs, isShopifyPricingRuleView, shopifyPricingRuleTabs, isShopifyCustomerView, shopifyCustomerTabs, tabs]);

  // ============================================
  // RENDER
  // ============================================
  
  // Get language for remount safety
  const { language } = useLanguage();

  // Guard: Ensure meta and formData are valid (AFTER all hooks to satisfy Rules of Hooks)
  if (!isValidMeta) {
    if (import.meta.env.DEV) {
      console.error('[ExperienceView] Invalid meta schema:', meta);
    }
    return (
      <div className="experience-view" key={language}>
        <div className="text-destructive p-8 text-center">
          Invalid schema configuration. Please check the view setup.
        </div>
      </div>
    );
  }

  // ✅ SAFE: Keep layout, switch content inside
  // If Dashboard tab is active, render it instead of TabRenderer
  if (activeTab === DASHBOARD_TAB_ID) {
    return (
      <div className="experience-view" key={language}>
        <DocDashboardTab 
          doctype={doctype} 
          name={docName || ''} 
          viewMode="experience"
          viewStyle={view.style}
        />
      </div>
    );
  }
  
  // If Connection (Quick Links) tab is active, render it instead of TabRenderer
  if (activeTab === QUICK_CONNECTIONS_TAB_ID) {
    return (
      <div className="experience-view" key={language}>
        <QuickConnectionsTab doctype={doctype} name={docName || ''} />
      </div>
    );
  }
  
  // If Item Price tab is active, render it instead of TabRenderer
  if (activeTab === ITEM_PRICE_TAB_ID) {
    return (
      <div className="experience-view" key={language}>
        <ItemPriceTab doctype={doctype} name={docName || ''} />
      </div>
    );
  }

  // If Loyalty tab is active, render it (Customer only)
  if (activeTab === LOYALTY_TAB_ID) {
    return (
      <div className="experience-view" key={language}>
        <LoyaltyTab
          customer={docName || ''}
          customerName={formData?.customer_name}
          tier={formData?.loyalty_program_tier}
          program={formData?.loyalty_program}
          storefrontKey={formData?.custom_primary_storefront}
        />
      </div>
    );
  }

  // If Addresses tab is active, render it (Customer only)
  if (activeTab === ADDRESSES_TAB_ID) {
    return (
      <div className="experience-view" key={language}>
        <AddressesTab customer={docName || ''} />
      </div>
    );
  }

  // If Financial tab is active, render the financial cockpit (Customer only).
  // Wrapped in DynamicViewProvider so the embedded MetaFieldEditModal can
  // resolve the Customer doctype for its direct patch-save.
  if (isShopifyCustomerView && activeTab === 'financial' && !isNew) {
    return (
      <div className="experience-view" key={language}>
        <DynamicViewProvider
          doctype={meta.name}
          mode="experience"
          style={view.style}
          enableSystemTranslations={false}
        >
          <FinancialTab
            customer={docName || ''}
            formData={formData || {}}
            metaFields={meta.fields}
            onFieldChange={onFieldChange}
            onSaveSuccess={onSaveSuccess}
            documentName={documentName}
          />
        </DynamicViewProvider>
      </div>
    );
  }


  // If Connections tab is active, render it instead of TabRenderer
  // Pass connectionGroups from DocViewSchema if available
  if (activeTab === CONNECTIONS_TAB_ID) {
    const connectionGroups = docViewSchema?.views.experience?.connectionGroups;
    return (
      <div className="experience-view p-6" key={language}>
        <ConnectionsTab doctype={doctype} name={docName} connectionGroups={connectionGroups} />
      </div>
    );
  }

  // Guard: Ensure we have tabs to render, otherwise show a fallback
  if (tabsToRender.length === 0) {
    if (import.meta.env.DEV) {
      console.warn('[ExperienceView] No tabs to render. This may indicate a schema or data issue.');
    }
    return (
      <div className="experience-view" key={language}>
        <div className="text-muted-foreground p-8 text-center">
          No content available. Please check the view configuration.
        </div>
      </div>
    );
  }

  return (
    <div className="experience-view group/detail" key={language} data-detail-density={effectiveDensity} data-detail-textsize={detailTextSize}>
      {/* All sections including header - rendered through TabRenderer */}
      <DynamicViewProvider
        doctype={meta.name}
        mode="experience"
        style={view.style}
        enableSystemTranslations={false}
      >
        <TabRenderer
          tabs={tabsToRender}
          formData={formData}
          onFieldChange={onFieldChange}
          disabled={disabled}
          onSave={onSave}
          onSubmit={onSubmit}
          onSaveSuccess={onSaveSuccess}
          isSaving={isSaving}
          resetKey={resetKey}
          layoutStyle={layoutStyle}
          viewMode="experience" // Experience view is always experience mode
          meta={meta}
          experienceViewStyle={
            view.style === 'shopify' ? 'shopify' :
            view.style === 'apple' ? 'apple' :
            undefined
          }
          documentName={documentName}
          isNew={isNew}
          fieldErrors={fieldErrors}
          onClearFieldError={onClearFieldError}
        />

        {/* Custom Field Bucket - only for legacy FormViewConfig doctypes */}
        {/* Schema-driven doctypes use _collectUnmapped sections instead */}
        {/* Must be inside DynamicViewProvider because FieldRenderer uses useDynamicView() */}
        {shouldShowCustomFieldBucket && (
          <div className="mt-8">
            <CustomFieldBucket
              fields={unmappedFields}
              formData={formData}
              onFieldChange={onFieldChange}
              disabled={disabled}
              config={view.formView!.customFieldBucket}
              resetKey={resetKey}
            />
          </div>
        )}

        {/* Relation Widgets */}
        {view.formView?.relationWidgets && view.formView.relationWidgets.length > 0 && (
          <div className="mt-8 space-y-6">
            {view.formView.relationWidgets.map((widget) => (
              <RelationWidget
                key={widget.id}
                widget={widget}
                sourceDoc={formData}
              />
            ))}
          </div>
        )}
      </DynamicViewProvider>
    </div>
  );
}
